import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { Menu, X, ChevronDown, Camera, Radio, Shield } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const { settings } = useGlobal();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const isActive = (path) => location.pathname === path;
  const isSolutionActive = () => ['/mobility-imaging', '/surveillance', '/ultrasonic'].includes(location.pathname);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSolutionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setSolutionsOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const solutions = [
    { path: '/mobility-imaging', label: 'Mobility Imaging', desc: 'Dashcams, MDVRs, Vehicle Cameras', icon: Camera },
    { path: '/surveillance', label: 'Surveillance', desc: 'IP/Analog Cameras, NVRs, Kits', icon: Shield },
    { path: '/ultrasonic', label: 'Ultrasonic Deterrence', desc: 'Bird & Animal Control Systems', icon: Radio },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1f2e]" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={settings.logo_url} 
              alt="Anchilex" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <Link
              to="/"
              className={`relative py-2 text-[15px] font-medium transition-colors duration-200 ${
                isActive('/') ? 'text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Home
              {isActive('/') && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-white rounded-full" />
              )}
            </Link>

            {/* Solutions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setSolutionsOpen(!solutionsOpen)}
                className={`flex items-center gap-1 py-2 text-[15px] font-medium transition-colors duration-200 ${
                  isSolutionActive() ? 'text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Solutions
                <ChevronDown className={`w-4 h-4 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                {isSolutionActive() && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-white rounded-full" />
                )}
              </button>

              {/* Dropdown Menu */}
              {solutionsOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-[#232a3b] border border-[#3d4758] rounded-xl shadow-xl overflow-hidden">
                  {solutions.map((solution) => (
                    <Link
                      key={solution.path}
                      to={solution.path}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-[#2a324a] transition-colors ${
                        isActive(solution.path) ? 'bg-[#2a324a]' : ''
                      }`}
                    >
                      <solution.icon className="w-5 h-5 text-[#8b5cf6] mt-0.5" />
                      <div>
                        <div className="text-white text-sm font-medium">{solution.label}</div>
                        <div className="text-slate-500 text-xs">{solution.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/engineering"
              className={`relative py-2 text-[15px] font-medium transition-colors duration-200 ${
                isActive('/engineering') ? 'text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              OEM/ODM
              {isActive('/engineering') && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-white rounded-full" />
              )}
            </Link>

            <Link
              to="/about"
              className={`relative py-2 text-[15px] font-medium transition-colors duration-200 ${
                isActive('/about') ? 'text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              About
              {isActive('/about') && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-white rounded-full" />
              )}
            </Link>
          </nav>

          {/* Contact Button */}
          <div className="hidden lg:flex items-center">
            <Link 
              to="/contact"
              className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white text-[15px] font-medium px-6 py-2.5 rounded-full transition-colors duration-200"
              data-testid="contact-btn"
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Subtle bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#1a1f2e] border-t border-slate-800" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-2">
            <Link
              to="/"
              className="block text-slate-300 hover:text-white py-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            {/* Mobile Solutions Section */}
            <div className="py-2">
              <div className="text-white font-medium mb-2">Solutions</div>
              <div className="pl-4 space-y-2 border-l border-slate-700">
                {solutions.map((solution) => (
                  <Link
                    key={solution.path}
                    to={solution.path}
                    className="block text-slate-400 hover:text-white py-1 text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {solution.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/engineering"
              className="block text-slate-300 hover:text-white py-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              OEM/ODM
            </Link>
            
            <Link
              to="/about"
              className="block text-slate-300 hover:text-white py-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>

            <div className="pt-4 border-t border-slate-800">
              <Link 
                to="/contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center bg-[#5b3589] hover:bg-[#6d3d9e] text-white font-medium px-6 py-3 rounded-full"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
